import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${PM_SERVICE_BASE_URL}/api/v1/projects`;

export default function useProjects(page=0, pageSize=100) {
  const authToken = sessionStorage.getItem("authToken");
  const [projects, setProjects] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // fetch all projects
  const fetchAllProjects = useCallback(async () => {
    if (!authToken) return;
    if (initialLoad) setLoading(true);
    else setLoading(true);

    try {
      const res = await apiClient.get(
        `${BASE_URL}?page=${page}&size=${pageSize}`,
      );

      setProjects(res.data.content);
      setTotalPages(res.data.totalPages || 1);
      setTotalElements(res.data.totalElements || 0);
    } catch (error) {
      handleAxiosErrors(error, "fetch all projects");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [authToken, page, pageSize]);

  // fetch project by id
  const fetchProjectById = useCallback(
    async (projectId) => {
      if (!authToken) return;
      setLoading(true);

      try {
        const res = await apiClient.get(`${BASE_URL}/${projectId}`);
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "fetch project by id");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // create project
  const createProject = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const actorId = sessionStorage.getItem("actorId") || sessionStorage.getItem("userId");
        const res = await apiClient.post(BASE_URL, payload, {
          headers: actorId ? { "O-ACTOR-ID": actorId } : {}
        });

        setProjects((prev) => [res.data, ...prev]);
        toast.success("Project created successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "create project");
        throw error;
      }
    },
    [authToken],
  );

  // update project
  const updateProject = useCallback(
    async (projectId, payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.put(`${BASE_URL}/${projectId}`, payload);

        setProjects((prev) =>
          prev.map((project) =>
            project.id === projectId ? res.data : project,
          ),
        );
        toast.success("Project updated successfully");
      } catch (error) {
        handleAxiosErrors(error, "update project");
      }
    },
    [authToken],
  );

  // delete project
  const deleteProject = useCallback(
    async (projectId) => {
      if (!authToken) return;
      try {
        await apiClient.delete(`${BASE_URL}/${projectId}`);

        setProjects((prev) =>
          prev.filter((project) => project.id !== projectId),
        );
        toast.success("Project deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete project");
      }
    },
    [authToken],
  );

  // fetch tasks by project
  const fetchTasksByProject = useCallback(
    async (projectId) => {
      if (!authToken || !projectId) return [];

      setLoading(true);
      try {
        const res = await apiClient.get(`${BASE_URL}/${projectId}/tasks`);

        return res.data.content;
      } catch (error) {
        handleAxiosErrors(error, "fetch tasks by project");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // fetch tasks by project iteration
  const fetchTasksByProjectIteration = useCallback(
    async (projectId, iterationId) => {
      if (!authToken || !projectId) return [];

      setLoading(true);
      try {
        const res = await apiClient.get(`${BASE_URL}/${projectId}/tasks`, { params: { iterationId } });

        return res.data.content;
      } catch (error) {
        handleAxiosErrors(error, "fetch tasks by project iteration");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchAllProjects();
  }, [authToken, fetchAllProjects]);

  return {
    projects,
    loading,
    initialLoad,
    fetchProjectById,
    fetchTasksByProject,
    fetchTasksByProjectIteration,
    createProject,
    updateProject,
    deleteProject,
    totalPages,
    totalElements,
  };
}
